import { ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

export type StepperProps = {
    /** Labels of all steps, in order. */
    steps: readonly string[];
    /** Index of the current step. */
    step: number;
    /** Called with the step index when a navigable step is clicked. */
    onStepClick?: (step: number) => void;
    /**
     * Whether a given step can be navigated to. Defaults to allowing only the
     * already-completed steps before the current one. Provide this to also
     * unlock steps ahead of the current one, e.g. sections already filled in.
     */
    isStepEnabled?: (index: number) => boolean;
    className?: string;
};

/**
 * Breadcrumb-style progress indicator for multi-step forms.
 * Navigable steps are highlighted and can be clicked to jump to them.
 */
export const Stepper = ({
    steps,
    step,
    onStepClick,
    isStepEnabled,
    className,
}: StepperProps): JSX.Element => (
    <div className={cn("flex items-center gap-2", className)}>
        {steps.map((label, index) => {
            const enabled = isStepEnabled ? isStepEnabled(index) : index < step;

            return (
                <div key={label} className="flex items-center gap-2">
                    {index > 0 && <ChevronRight className="text-muted-foreground size-4" />}
                    <button
                        type="button"
                        disabled={index === step || !enabled}
                        onClick={() => onStepClick?.(index)}
                        className={cn(
                            "flex items-center gap-2",
                            index !== step && enabled && "cursor-pointer",
                            index !== step && !enabled && "text-muted-foreground",
                        )}
                    >
                        <span
                            className={cn(
                                "flex size-7 items-center justify-center rounded-full text-sm font-medium",
                                index === step
                                    ? "bg-input text-foreground"
                                    : enabled
                                      ? "bg-primary text-primary-foreground"
                                      : "bg-background text-muted-foreground border-border border",
                            )}
                        >
                            {index + 1}
                        </span>
                        {label}
                    </button>
                </div>
            );
        })}
    </div>
);
